import {
    Component,
    Injector,
    NgZone,
    ViewChild,
    OnDestroy
} from "@angular/core";
import { NavController, Platform } from "@ionic/angular";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar } from "@ionic-native/status-bar/ngx";
import { AuthService, authStateKey } from "./services/auth/auth.service";
import { map, skip, tap } from "rxjs/operators";
import { Subscription } from "rxjs";
import { AuthState } from "./entities/AuthState";
import { AuthUser } from "./entities/AuthUser";
import { Storage } from "@ionic/storage-angular";
import { TranslateService } from "@ngx-translate/core";
import { HelpersModule } from "./modules/helpers/helpers.module";
import moment from "moment";
import { PushNotifications } from "@capacitor/push-notifications";
import { AccountsService } from "./services/accounts/accounts.service";
import { environment } from "../environments/environment";
import JSConfetti from "js-confetti";
import { Router } from "@angular/router";
import { register } from "swiper/element/bundle";
import * as Sentry from "@sentry/capacitor";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { fetchCapgoFailureReason } from "./services/capgo/capgo-update-failure";
import { AlertService } from "./services/alert/alert.service";
import { ModalController } from "@ionic/angular";
import { LanguageSelectionModalComponent } from "./components/language-selection-modal/language-selection-modal.component";
import { Model } from "./entities/Concers/Model";
import { RedirectUrlService } from "./services/redirect-url/redirect-url.service";

const lastViewedBirthdayDate = "lastViewedBirthdayDate";

register();

@Component({
    selector: "app-root",
    templateUrl: "app.component.html",
    styleUrls: ["app.component.scss"]
})
export class AppComponent implements OnDestroy {
    private static readonly LAST_ACTIVE_DATE_KEY = "lastActiveDate";
    private static readonly AVAILABLE_LANGUAGES = [
        "pl",
        "en",
        "nl",
        "de",
        "fr"
    ];
    public date = null;
    public anniversary = null;
    public isReady = false;
    public isUpdating = false;
    public updatePercent = 0;
    private userProfileSubscription: Subscription;

    constructor(
        private platform: Platform,
        private statusBar: StatusBar,
        private authService: AuthService,
        private accountsService: AccountsService,
        private navController: NavController,
        private storage: Storage,
        private translate: TranslateService,
        private ngZone: NgZone,
        private router: Router,
        private alertService: AlertService,
        private modalController: ModalController,
        private injector: Injector,
        private redirectUrlService: RedirectUrlService
    ) {
        Model.setInjector(this.injector);
        this.initializeApp();
    }

    async initializeApp() {
        try {
            await this.platform.ready();

            // Get the stored date first
            this.date = await this.storage.get(
                AppComponent.LAST_ACTIVE_DATE_KEY
            );
            this.handleAppResume();

            const deviceLang = HelpersModule.getDeviceLanguage();

            this.translate.setDefaultLang("en");

            // Get auth state to check if user is logged in
            const storedAuthState = await this.storage.get(authStateKey);
            const authState = AuthState.makeFromObject(storedAuthState || {});

            // Use profile language if authenticated and valid
            let appLanguage = "en";
            if (
                authState.isAuthenticated() &&
                authState.authUser?.language_code
            ) {
                appLanguage = authState.authUser.language_code;
            } else if (AppComponent.AVAILABLE_LANGUAGES.includes(deviceLang)) {
                appLanguage = deviceLang;
            }

            await this.translate.use(appLanguage).toPromise();
            moment.locale(appLanguage);

            this.statusBar.styleDefault();

            if (!Capacitor.isNativePlatform()) {
                this.setRedirectUrl();
            }

            this.setAuthState();

            if (environment.production === true) {
                setTimeout(() => {
                    this.setPauseAppEvents();
                }, 1000);
            }

            CapacitorUpdater.addListener("download", (event) => {
                this.ngZone.run(() => {
                    this.isUpdating = true;
                    this.updatePercent = event.percent;
                });
            });
            CapacitorUpdater.addListener("downloadComplete", (event) => {
                this.ngZone.run(() => {
                    this.updatePercent = 100;
                });
                const version = event.bundle.id;
                CapacitorUpdater.set({
                    id: version
                }).catch((error) => {
                    this.ngZone.run(() => {
                        this.isUpdating = false;
                        this.updatePercent = 0;
                    });
                    Sentry.captureException(error, {
                        extra: {
                            message: "Failed to apply update"
                        }
                    });
                });
            });
            CapacitorUpdater.addListener("updateFailed", (event) => {
                this.ngZone.run(() => {
                    this.isUpdating = false;
                    this.updatePercent = 0;
                });
                Sentry.captureException(new Error("Update failed"), {
                    extra: {
                        event
                    }
                });
            });
            CapacitorUpdater.addListener("downloadFailed", (event) => {
                this.ngZone.run(() => {
                    this.isUpdating = false;
                    this.updatePercent = 0;
                });
                fetchCapgoFailureReason(event?.version).then((failure) => {
                    if (failure.expected) {
                        Sentry.addBreadcrumb({
                            category: "capgo",
                            message: `No update: ${failure.reason}`,
                            data: { event, failure },
                            level: "info"
                        });
                        return;
                    }

                    Sentry.withScope((scope) => {
                        scope.setTag("capgo_reason", failure.reason);
                        scope.setFingerprint([
                            "capgo-download-failed",
                            failure.reason
                        ]);
                        scope.setExtra("event", event);
                        scope.setExtra("failure", failure);
                        Sentry.captureException(
                            new Error(`Download failed: ${failure.reason}`)
                        );
                    });
                });
            });
        } catch (error) {
            console.error("Error during initialization: ", error);
            Sentry.captureException(error, {
                extra: {
                    message: "Error during initialization"
                }
            });
        }

        this.isReady = true;
        SplashScreen.hide();
    }

    /**
     * Set application authentication state
     */
    setAuthState() {
        this.authService.authState
            .pipe(
                tap((authState: AuthState) => {
                    if (
                        !authState.isChanged() ||
                        this.router.url === "/public/privacy-policy"
                    ) {
                        return;
                    }

                    if (!authState.isAuthenticated()) {
                        if (!authState.hasPhone()) {
                            this.navController.navigateForward([
                                `/request-login`
                            ]);
                        }

                        if (
                            authState.hasPhone() &&
                            !authState.hasTwoFactorToken()
                        ) {
                            this.navController.navigateForward([`/sms-login`]);
                        }

                        if (
                            authState.hasTwoFactorToken() &&
                            !authState.hasSetPin()
                        ) {
                            if (!authState.pin) {
                                this.navController.navigateRoot(["/set-pin"]);
                            } else {
                                this.navController.navigateForward([
                                    "/confirm-pin"
                                ]);
                            }
                        }

                        if (
                            authState.hasTwoFactorToken() &&
                            authState.hasSetPin()
                        ) {
                            this.navController.navigateRoot(["/login"]);
                        }
                    }

                    if (authState.isAuthenticated()) {
                        if (authState.isRefreshTokenExpired()) {
                            this.navController.navigateRoot([`/login`]);
                            return;
                        }

                        if (!authState.authUser.has_accepted_terms) {
                            this.navController.navigateRoot([
                                "/terms-and-conditions"
                            ]);
                            return;
                        }

                        // Subscribe to user profile changes to update language.
                        // Wired before the language gate below, so a profile
                        // that changes while the modal is open - or that the
                        // startup refresh brings in later - still updates the
                        // language.
                        if (!this.userProfileSubscription) {
                            this.userProfileSubscription =
                                this.authService.userProfileState.subscribe(
                                    (authUser: AuthUser) => {
                                        if (authUser) {
                                            this.setLanguageFromUser(authUser);
                                        }
                                    }
                                );
                        }

                        // Check if user needs language selection
                        if (
                            authState.authUser.needsLanguageSelection() &&
                            authState.authUser.available_languages?.length > 0
                        ) {
                            this.showLanguageSelectionModal();
                            return; // Don't continue until language is selected
                        }

                        // Update language based on user's profile preference
                        this.setLanguageFromUser(authState.authUser);

                        this.grantFirebasePermissions();
                        this.addPushNotificationListeners();
                        this.loggedIn();
                    }
                }),
                map((authState: AuthState) => {
                    authState.authChanged = false;

                    return authState;
                })
            )
            .subscribe((authState: AuthState) => {
                this.storage.set(authStateKey, authState);
            });
    }

    /**
     * Triggered functions after application is paused or resumed.
     */
    setPauseAppEvents() {
        // Handle mobile platform events
        this.platform.pause.subscribe(() => {
            this.handleAppPause();
        });

        this.platform.resume.subscribe(() => {
            this.handleAppResume();
        });

        // Handle browser visibility events
        if (!Capacitor.isNativePlatform()) {
            document.addEventListener("visibilitychange", () => {
                if (document.hidden) {
                    this.handleAppPause();
                } else {
                    this.handleAppResume();
                }
            });
        }
    }

    private handleAppPause() {
        // Set last active date in UTC
        this.date = new Date().toISOString();
        // Store the date
        this.storage.set(AppComponent.LAST_ACTIVE_DATE_KEY, this.date);
    }

    private handleAppResume() {
        this.ngZone.run(async () => {
            // Redirect to login on resume when the app has been inactive for longer than 5 minutes.
            if (
                !this.date ||
                moment
                    .utc(this.date)
                    .isBefore(moment.utc().subtract(24, "hours"))
            ) {
                if (this.authService.isAuthenticated()) {
                    this.authService.logout();
                }
                this.date = new Date().toISOString();
                this.storage.set(AppComponent.LAST_ACTIVE_DATE_KEY, this.date);
            }
        });
    }

    async grantFirebasePermissions() {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        if (!this.authService.isUserActive()) {
            return;
        }

        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === "prompt") {
            permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== "granted") {
            return false;
        }

        await PushNotifications.register();
    }

    async addPushNotificationListeners() {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        await PushNotifications.addListener("registration", (token) => {
            this.accountsService.storeDeviceToken(token.value).subscribe();
        });

        await PushNotifications.addListener("registrationError", (err) => {
            console.error("Registration error: ", err.error);
        });

        await PushNotifications.addListener(
            "pushNotificationReceived",
            (notification) => {
                console.log("Push notification received: ", notification);
            }
        );

        await PushNotifications.addListener(
            "pushNotificationActionPerformed",
            async (notification) => {
                const link = notification.notification?.data?.link;

                if (link) {
                    await this.handleNotificationUrl(link);
                }
            }
        );
    }

    async setRedirectUrl() {
        const params = new URL(window.location.href).searchParams;
        const redirectUrl = params.get("redirectUrl");

        if (redirectUrl) {
            const url = new URL(redirectUrl);
            const normalizedUrl = url.pathname + url.search;
            await this.redirectUrlService.set(normalizedUrl);
        }
    }

    private normalizeUrl(url: string): string | null {
        try {
            if (url.startsWith("http://") || url.startsWith("https://")) {
                const urlObj = new URL(url);
                return urlObj.pathname + urlObj.search;
            } else if (url.startsWith("/")) {
                return url;
            } else {
                return `/${url}`;
            }
        } catch (error) {
            console.error("Error processing notification URL:", url);
            Sentry.captureException(new Error("Invalid notification URL"), {
                extra: {
                    message: "Error processing notification URL",
                    url: url
                }
            });

            throw new Error("Invalid notification URL");
        }
    }

    private async handleNotificationUrl(url: string) {
        const normalizedUrl = this.normalizeUrl(url);

        if (!normalizedUrl) {
            return;
        }

        if (this.authService.isAuthenticated()) {
            this.ngZone.run(() => {
                this.navController.navigateForward([normalizedUrl]);
            });
        } else {
            await this.redirectUrlService.set(normalizedUrl);
        }
    }

    loggedIn() {
        this.loggedIn = () => {};
        this.authService.todayAnniversaries
            .asObservable()
            .pipe(
                skip(1),
                tap(async (data) => {
                    // Check whether the user has viewed the birthday notification today
                    // By comparing the current date with the last viewed date from the storage
                    const currentDate = moment().format("YYYY-MM-DD");
                    const lastViewedDate =
                        (await this?.storage?.get(lastViewedBirthdayDate)) ??
                        null;

                    if (lastViewedDate !== currentDate) {
                        this.showTodayAnniversaries(data);

                        this.storage.set(lastViewedBirthdayDate, currentDate);
                    }
                })
            )
            .subscribe();

        this.authService.updateTodayAnniversaries();
    }

    showTodayAnniversaries(data) {
        if (
            data.date !== moment().format("YYYY-MM-DD") ||
            data.isNotified ||
            !data.items.length
        ) {
            return;
        }

        // Check if the authenticated user has anniversary
        const authUserId = this.authService.userProfileState.getValue().id;
        const isAuthUserAnniversary = data.items.find((i) => {
            return (
                i.id === authUserId &&
                moment().format("MM-DD") ===
                    i.getMomentEmploymentStartDate().format("MM-DD")
            );
        });
        this.anniversary = isAuthUserAnniversary
            ? [isAuthUserAnniversary]
            : data.items;
        this.anniversary.self = !!isAuthUserAnniversary;
        this.anniversary.firstPersons = data.items
            .slice(0, -1)
            .map((i) => i.name)
            .join(", ");
        this.anniversary.lastPerson = data.items.slice(-1).pop().name;

        this.authService.markTodayAnniversariesAsRead();

        this.setConfetti();
    }

    setConfetti() {
        setTimeout(() => {
            const jsConfetti = new JSConfetti();
            jsConfetti.addConfetti();
        }, 1500);
    }

    moreConfetti() {
        const jsConfetti = new JSConfetti();
        jsConfetti.addConfetti();
    }

    ngOnDestroy() {
        this.userProfileSubscription?.unsubscribe();
    }

    /**
     * Sets the application language based on user preferences or device language
     * @param authUser The authenticated user object
     * @param deviceLang Optional device language as fallback
     */
    private setLanguageFromUser(
        authUser: AuthUser | null,
        deviceLang?: string
    ): void {
        if (authUser?.language_id) {
            // Get language code from business unit languages
            const userLanguage = authUser.language_code;
            if (userLanguage) {
                this.useLanguage(userLanguage);
            } else {
                console.warn(
                    "User language not found in business unit languages"
                );
            }
        } else if (
            deviceLang &&
            AppComponent.AVAILABLE_LANGUAGES.includes(deviceLang)
        ) {
            this.useLanguage(deviceLang);
        }
    }

    /**
     * Switches the application language, unless it is already in use.
     *
     * The profile state emits on every profile change, so switching
     * unconditionally would reload the translations - and re-emit
     * onLangChange - on every one of them.
     */
    private useLanguage(language: string): void {
        if (this.translate.currentLang === language) {
            return;
        }

        console.log("Setting language to:", language);
        this.translate.use(language);
    }

    async showLanguageSelectionModal() {
        const authUser = this.authService.userProfileState.getValue();

        // Don't show modal if no languages are available
        if (!authUser?.available_languages?.length) {
            console.error("No languages available for selection");
            return;
        }

        const modal = await this.modalController.create({
            component: LanguageSelectionModalComponent,
            componentProps: {
                authUser: authUser
            },
            backdropDismiss: false, // Force selection
            cssClass: "language-selection-modal"
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data?.languageUpdated) {
            // The modal already refreshed the profile into the auth state, so
            // only the pipeline still needs waking. Re-reading storage here
            // would race the write that persists that same profile.
            this.authService.resumeAuthState();
        }
    }
}
